import { PlacesType } from 'react-tooltip';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import styled, { css } from 'styled-components';

import { colors } from '@controlrooms/design-tokens';

interface StyledTooltipProps {
  delayShow?: number | string;
  place?: PlacesType;
  _theme?: string;
  type?: 'primary' | 'secondary';
}

export const StyledTooltip = styled.span.attrs<StyledTooltipProps>((props) => ({
  'data-effect': 'solid',
  'data-background-color':
    props.type === 'secondary'
      ? props._theme === 'light'
        ? props.theme.tooltip.backgroundColorAltDark
        : props.theme.tooltip.backgroundColorAlt
      : props._theme === 'light'
      ? props.theme.tooltip.backgroundColorDark
      : props.theme.tooltip.backgroundColor,
  'data-text-color':
    props.type === 'secondary'
      ? props._theme === 'light'
        ? props.theme.tooltip.textColorAltDark
        : props.theme.tooltip.textColorAlt
      : props._theme === 'light'
      ? props.theme.tooltip.textColorDark
      : props.theme.tooltip.textColor,
  'data-delay-show': props.delayShow || '500',
  'data-place': props.place,
  'data-padding': '4px 8px',
}))<StyledTooltipProps>`
  flex-grow: 1;
`;

export interface CommonTooltipProps {
  id: string;
  place?: PlacesType;
  theme?: 'primary' | 'secondary';
  delayShow?: number;
}

export const StyledCommonTooltip = styled(ReactTooltip).attrs<CommonTooltipProps>((props) => ({
  effect: 'solid',
  backgroundColor:
    props.theme === 'secondary'
      ? props.theme.tooltip.backgroundColorAlt
      : props.theme.tooltip.backgroundColor,
  textColor:
    props.theme === 'secondary' ? props.theme.tooltip.textColorAlt : props.theme.tooltip.textColor,
  delayShow: props.delayShow || 500,
  place: props.place || 'top',
  padding: '4px 8px',
  id: props.id,
}))<StyledTooltipProps>`
  flex-grow: 1;
`;

// V2 Tooltip Components
interface TooltipV2Props {
  direction?: 'right' | 'left' | 'up' | 'down';
  tooltipType?: 'text' | 'actionable';
}

export const TooltipV2Container = styled.span`
  position: relative;
  display: inline-block;

  &:hover > div {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }
`;

export const TooltipBubble = styled.div<TooltipV2Props>`
  position: absolute;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 0.3s ease, visibility 0.3s ease;
  z-index: 1000;

  background: ${colors.k[100]};
  border-radius: 8px;
  box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.15);
  padding: ${({ tooltipType }) => (tooltipType === 'actionable' ? '8px' : '8px 16px')};
  max-width: 280px;
  width: max-content;

  ${({ direction }) => {
    switch (direction) {
      case 'right':
        return css`
          left: calc(100% + 8px);
          top: 50%;
          transform: translateY(-50%);
        `;
      case 'left':
        return css`
          right: calc(100% + 8px);
          top: 50%;
          transform: translateY(-50%);
        `;
      case 'up':
        return css`
          bottom: calc(100% + 8px);
          left: 50%;
          transform: translateX(-50%);
        `;
      case 'down':
        return css`
          top: calc(100% + 8px);
          left: 50%;
          transform: translateX(-50%);
        `;
      default:
        return css`
          left: calc(100% + 8px);
          top: 50%;
          transform: translateY(-50%);
        `;
    }
  }}
`;

export const TooltipArrow = styled.div<{ direction: 'right' | 'left' | 'up' | 'down' }>`
  position: absolute;
  width: 0;
  height: 0;

  ${({ direction }) => {
    switch (direction) {
      case 'right':
        return css`
          left: -6px;
          top: 50%;
          transform: translateY(-50%);
          border-top: 6px solid transparent;
          border-bottom: 6px solid transparent;
          border-right: 6px solid ${colors.k[100]};
        `;
      case 'left':
        return css`
          right: -6px;
          top: 50%;
          transform: translateY(-50%);
          border-top: 6px solid transparent;
          border-bottom: 6px solid transparent;
          border-left: 6px solid ${colors.k[100]};
        `;
      case 'up':
        return css`
          bottom: -6px;
          left: 50%;
          transform: translateX(-50%);
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-top: 6px solid ${colors.k[100]};
        `;
      case 'down':
        return css`
          top: -6px;
          left: 50%;
          transform: translateX(-50%);
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-bottom: 6px solid ${colors.k[100]};
        `;
      default:
        return css`
          left: -6px;
          top: 50%;
          transform: translateY(-50%);
          border-top: 6px solid transparent;
          border-bottom: 6px solid transparent;
          border-right: 6px solid ${colors.k[100]};
        `;
    }
  }}
`;

export const TooltipContent = styled.div<TooltipV2Props>`
  display: flex;
  align-items: center;
  gap: ${({ tooltipType }) => (tooltipType === 'actionable' ? '16px' : '4px')};

  ${({ tooltipType }) =>
    tooltipType === 'actionable' &&
    css`
      flex-direction: row;
    `}
`;

export const TooltipText = styled.span<TooltipV2Props>`
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-size: 11px;
  line-height: 1.18;
  color: ${colors.content['neutral-6']};
  text-align: left;

  ${({ tooltipType }) =>
    tooltipType === 'actionable' &&
    css`
      flex: 1;
    `}
`;

export const TooltipIconContainer = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;

  svg {
    fill: ${colors.content['neutral-6']};
  }
`;

export const TooltipActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`;
